import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";
import { PLAY_COLORS, playSearchUrl } from "./PlayIntro";

const EVENT_CARDS = [
  {
    key: "host",
    badge: "Host",
    badgeClass: "",
    badgeColor: PLAY_COLORS.events,
    dot: "#a5b4fc",
    image: "/figma-assets/play/events-host.jpg",
    title: "Host an Event",
    items: [
      "Event space listings",
      "Corporate and private options",
      "Capacity and amenity filters",
      "Real-time availability",
    ],
    cta: "Find an Event Space",
  },
  {
    key: "attend",
    badge: "Attend",
    badgeClass: "border-[1px] border-solid border-white/30 bg-white/20",
    badgeColor: undefined,
    dot: "#ffd230",
    image: "/figma-assets/play/events-attend.jpg",
    title: "Attend an Event",
    items: [
      "Upcoming events near you",
      "Networking and conferences",
      "Community gatherings",
      "Secure online ticket booking",
    ],
    cta: "Find an Event",
  },
];

const EVENT_POINTS = [
  "Simple venue discovery and booking for hosts",
  "Easy discovery of relevant events to attend",
  "Spaces and events for every occasion",
  "One platform for meetings, conferences and celebrations",
];

export default function PlayEvents({ subcategories }: { subcategories: ListingSubcategory[] }) {
  const history = useHistory();

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p
          className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px]"
          style={{ color: PLAY_COLORS.events }}
        >
          SPACIA PLAY
        </p>
        <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
          Events
        </h2>
        <p className="max-w-[672px] pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
          Find the right space to host your own event, or discover events happening around you and book your place to
          attend — from business conferences and networking meetups to community and social gatherings.
        </p>

        <div className="grid grid-cols-1 gap-6 pt-12 lg:grid-cols-2">
          {EVENT_CARDS.map(card => (
            <div key={card.key} className="relative flex h-[360px] items-end overflow-hidden rounded-3xl">
              <img src={card.image} alt={card.title} className="absolute inset-0 size-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="relative p-8">
                <span
                  className={`inline-block rounded-full px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white ${card.badgeClass}`}
                  style={card.badgeColor ? { backgroundColor: card.badgeColor } : undefined}
                >
                  {card.badge}
                </span>
                <h3 className="pt-[12px] text-[20px] font-bold leading-7 text-white">{card.title}</h3>
                <ul className="space-y-1.5 pt-[12px]">
                  {card.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-[14px] leading-5 text-white/80">
                      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: card.dot }} />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => history.push(playSearchUrl("events", subcategories))}
                  className="mt-[16px] inline-flex items-center gap-2 text-[14px] font-semibold leading-5 text-white hover:underline"
                >
                  {card.cta}
                  <ArrowRight className="size-[15px]" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-[16px] pt-12 sm:grid-cols-2 lg:grid-cols-4">
          {EVENT_POINTS.map(point => (
            <div
              key={point}
              className="flex flex-col items-start gap-[12px] rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-[#f5f8ff] p-[20px]"
            >
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: `${PLAY_COLORS.events}1a` }}
              >
                <ArrowRight className="size-3" style={{ color: PLAY_COLORS.events }} />
              </span>
              <p className="text-[14px] leading-[23px] text-[#717182]">{point}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
